import { ChevronDown, Ellipsis, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { plural } from '../lib/format.ts';
import { motionPresets } from '../lib/motionPresets.ts';
import { Button } from './Button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './DropdownMenu.tsx';
import { IconButton } from './IconButton.tsx';
import { Tooltip } from './Tooltip.tsx';

export interface BulkAction {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Required unless the action is a `menu`. */
  onClick?: () => void;
  /** Destructive: a danger button (the action itself still confirms). */
  danger?: boolean;
  disabled?: boolean;
  /** Why it is disabled, or what it does: shown on hover. */
  hint?: string;
  shortcut?: string[];
  /**
   * A choice to make first ("Set status" then "Ready"): the button opens these, above the bar.
   * With `onClick` as well, the button does the main thing and a chevron beside it holds these.
   */
  menu?: BulkMenuItem[];
  /**
   * The action a phone keeps on the bar; every other action moves into its More menu. Without
   * one, the first action that does something on a click (not only a menu, not destructive) is it.
   */
  primary?: boolean;
}

export interface BulkMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

export interface BulkBarProps {
  count: number;
  /** Noun for the count: "session" becomes "3 sessions selected". */
  noun?: string;
  actions: BulkAction[];
  onClear: () => void;
  /** Select every row (shown while only some are selected). */
  onSelectAll?: () => void;
  total?: number;
  /**
   * Stick to the bottom of the scrolling list it is placed in, centred on that column, instead of
   * floating over the content column (a page with a docked side panel keeps the panel clear).
   */
  inline?: boolean;
}

/** Set on the document element while a bulk bar is open, so other foot bars (the phone hand-off) step aside. */
export const BULK_OPEN_ATTR = 'data-bulk-open';

/**
 * The bottom-padding contract for lists under a floating bar. While the bar is open its host (the
 * nearest `[data-bulk-host]`, else the page's `main`) carries `--bulk-bar-space`, the bar's height
 * plus its 16 px inset above and below, as bottom padding, so the last rows scroll clear of it.
 * A list that scrolls inside its own box instead adds `pb-(--bulk-bar-space)` to that box.
 */
export const BULK_BAR_SPACE = '--bulk-bar-space';

/** The 16 px gutter between the bar and the edges of its column. */
const INSET = 16;

/** Phones: under 640 px only the primary action stays on the bar. */
const PHONE_QUERY = '(max-width: 39.99em)';

function subscribePhone(onChange: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {};
  const mq = matchMedia(PHONE_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function usePhone(): boolean {
  return useSyncExternalStore(subscribePhone, () => typeof matchMedia === 'function' && matchMedia(PHONE_QUERY).matches, () => false);
}

/**
 * The phone's one action: the flagged primary, else the first action that does something on a
 * click and is not destructive. A split action ("Start 3 missions" with its chevron) counts: it
 * stays on the bar whole, its chevron included.
 */
export function primaryBulkAction(actions: BulkAction[]): BulkAction | undefined {
  return actions.find((a) => a.primary) ?? actions.find((a) => a.onClick && !a.danger);
}

/**
 * Places a floating bar over its content column (never the rail): centred on the column, 16 px
 * from its sides and its foot, and reserves the bar's space at the foot of the column.
 */
function useColumnPlacement(open: boolean, inline: boolean | undefined) {
  const probe = useRef<HTMLSpanElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const root = document.documentElement;
    const n = Number(root.getAttribute(BULK_OPEN_ATTR) ?? 0) + 1;
    root.setAttribute(BULK_OPEN_ATTR, String(n));
    return () => {
      const left = Number(root.getAttribute(BULK_OPEN_ATTR) ?? 1) - 1;
      if (left > 0) root.setAttribute(BULK_OPEN_ATTR, String(left));
      else root.removeAttribute(BULK_OPEN_ATTR);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || inline) return;
    const host = probe.current?.closest<HTMLElement>('[data-bulk-host]') ?? probe.current?.closest<HTMLElement>('main') ?? null;
    if (!host) return;
    const before = host.style.paddingBottom;
    const measure = () => {
      const r = host.getBoundingClientRect();
      const width = Math.max(0, r.width - INSET * 2);
      setPlace({ left: r.left + INSET, width, bottom: Math.max(0, window.innerHeight - r.bottom) + INSET });
      const h = bar.current?.offsetHeight ?? 0;
      host.style.setProperty(BULK_BAR_SPACE, `${h + INSET * 2}px`);
      host.style.paddingBottom = `var(${BULK_BAR_SPACE})`;
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(host);
    if (bar.current) ro?.observe(bar.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      host.style.removeProperty(BULK_BAR_SPACE);
      host.style.paddingBottom = before;
    };
  }, [open, inline]);

  return { probe, bar, place };
}

/**
 * The floating action bar for bulk selection (DesignSystem 6.3): appears at the bottom of the
 * content column when rows are selected, names the count, offers the actions, and Esc clears it.
 * On a phone it keeps the count, the primary action and a More menu holding every other action,
 * so all of them stay reachable.
 */
export function BulkBar({ count, noun = 'item', actions, onClear, onSelectAll, total, inline }: BulkBarProps) {
  const open = count > 0;
  const phone = usePhone();
  const { probe, bar, place } = useColumnPlacement(open, inline);
  const canSelectAll = !!onSelectAll && total != null && count < total;
  const primary = phone ? primaryBulkAction(actions) : undefined;
  const rest = phone ? actions.filter((a) => a !== primary) : [];

  return (
    <>
      <span ref={probe} hidden />
      <AnimatePresence>
        {open && (
          <motion.div
            ref={bar}
            role="toolbar"
            aria-label={`Bulk actions for ${plural(count, noun)}`}
            data-bulk-bar=""
            {...motionPresets.arrive}
            className={cn(
              'pointer-events-auto z-(--z-toast) flex items-center gap-1 rounded-card border border-border bg-raised p-1 pl-3 shadow-elevation-2',
              inline ? 'sticky bottom-6 w-max max-w-full self-center' : 'fixed mx-auto',
              !inline && !place && 'inset-x-4 bottom-4 w-fit max-w-full',
            )}
            style={!inline && place ? { left: place.left, bottom: place.bottom, width: 'fit-content', maxWidth: place.width, right: 'auto', translate: `calc((${place.width}px - 100%) / 2) 0` } : undefined}
          >
            <span className="whitespace-nowrap pr-1 text-ui font-medium text-fg num" aria-live="polite">
              {/* The same words at every width ("12 items selected"). */}
              {`${plural(count, noun)} selected`}
            </span>
            {!phone && canSelectAll && (
              <Button size="sm" variant="ghost" onClick={onSelectAll}>
                Select all {total}
              </Button>
            )}
            <span className="mx-1 h-4 w-px shrink-0 bg-border" aria-hidden />
            {phone ? (
              <div className="flex min-w-0 items-center gap-1">
                {primary && <ActionButton a={primary} />}
                {(rest.length > 0 || canSelectAll) && <MoreMenu actions={rest} onSelectAll={canSelectAll ? onSelectAll : undefined} total={total} />}
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
                {actions.map((a) => (
                  <ActionButton key={a.id} a={a} />
                ))}
              </div>
            )}
            <span className="mx-1 h-4 w-px shrink-0 bg-border" aria-hidden />
            <IconButton label="Clear the selection" icon={<X />} size="sm" shortcut={['esc']} onClick={onClear} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ActionButton({ a }: { a: BulkAction }) {
  if (a.menu && a.onClick) {
    return (
      <span className="inline-flex items-center">
        <Tooltip content={a.hint ?? a.label} enabled={!!a.hint}>
          <span className="inline-flex">
            <Button size="sm" variant="ghost" icon={a.icon} disabled={a.disabled} onClick={a.onClick} className="rounded-r-none">
              {a.label}
            </Button>
          </span>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" icon={<ChevronDown />} aria-label={`More ways to ${a.label.toLowerCase()}`} className="rounded-l-none px-1" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end">
            {a.menu.map((m) => (
              <DropdownMenuItem key={m.id} icon={m.icon} disabled={m.disabled} onSelect={m.onSelect}>
                {m.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    );
  }
  if (a.menu) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" icon={a.icon} iconRight={<ChevronDown />} disabled={a.disabled}>
            {a.label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start">
          {a.menu.map((m) => (
            <DropdownMenuItem key={m.id} icon={m.icon} disabled={m.disabled} onSelect={m.onSelect}>
              {m.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <Tooltip content={a.hint ?? a.label} shortcut={a.shortcut} enabled={!!a.hint || !!a.shortcut}>
      <span className="inline-flex">
        <Button
          size="sm"
          variant="ghost"
          icon={a.icon}
          disabled={a.disabled}
          onClick={a.onClick}
          className={cn(a.danger && 'text-status-danger hover:bg-destructive-soft hover:text-destructive-ink')}
        >
          {a.label}
        </Button>
      </span>
    </Tooltip>
  );
}

/** A phone's overflow: every action but the primary, choices as in-place submenus, the destructive one last. */
function MoreMenu({ actions, onSelectAll, total }: { actions: BulkAction[]; onSelectAll?: () => void; total?: number }) {
  const plain = actions.filter((a) => !a.danger);
  const danger = actions.filter((a) => a.danger);
  const item = (a: BulkAction) => (
    <span key={a.id} className="contents">
      {a.onClick && (
        <DropdownMenuItem icon={a.icon} disabled={a.disabled} danger={a.danger} onSelect={a.onClick}>
          {a.label}
        </DropdownMenuItem>
      )}
      {a.menu && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={a.disabled}>
            {a.icon && <span className="flex [&_svg]:size-4">{a.icon}</span>}
            <span className="flex-1 truncate">{a.onClick ? `More ways to ${a.label.toLowerCase()}` : a.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {a.menu.map((m) => (
              <DropdownMenuItem key={m.id} icon={m.icon} disabled={m.disabled} onSelect={m.onSelect}>
                {m.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
    </span>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size="sm" label="More actions" icon={<Ellipsis />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end">
        {onSelectAll && total != null && <DropdownMenuItem onSelect={onSelectAll}>Select all {total}</DropdownMenuItem>}
        {plain.map(item)}
        {danger.length > 0 && (plain.length > 0 || onSelectAll) && <DropdownMenuSeparator />}
        {danger.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
